import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Calendar } from "@natscale/react-calendar";
import moment from "moment";
import { useMediaQuery } from "react-responsive";

import { Box, Flex } from "@/components/layout/flexbox";
import { isMobileQuery } from "@/components/layout/Media";
import TimeZoneSelector from "@/components/pieces/form/TimeZoneSelector";
import Clickable from "@/components/pieces/interaction/Clickable";
import AllTimeslotToggle from "@/components/pieces/schedule/SelectTimeSession/AllTimeslotToggle";
import { RequestBookingText } from "@/components/pieces/schedule/SelectTimeSession/shared";
import {
  TimeslotList,
  TimeZoneSelectorContainerStyled,
} from "@/components/pieces/schedule/SelectTimeSession/styles";
import TimeSlotSelector, {
  getNewSelectedTimeSlots,
  SelectedTimeSlots,
  TimeSlot,
} from "@/components/pieces/schedule/SelectTimeSession/TimeSlotSelector";
import {
  getTimeslotsByDate,
  mapCalendarDate,
} from "@/components/pieces/schedule/SelectTimeSession/utils";
import { CTA_COLOR, LIGHTEST_BACKGROUND } from "@/css/constants";
import { MiniUserType } from "@/graphql";

export interface DateTimeSlotSelectorProps {
  timeSlotData: TimeSlot[];
  loading: boolean;
  onChangeTimeZone: (option: { value: string }) => void;
  timeZone: string;
  coach?: Pick<MiniUserType, "id" | "firstName" | "lastName">;
  availableTimeSlotData: TimeSlot[];
  isCoachOwner?: boolean;
  onSelectedBookingTimes: (timeSlots: TimeSlot[]) => void;
  isMultiselect?: boolean;
  viewMode?: "vertical" | "horizontal";
  selectedSlots?: TimeSlot[];
  submitButton?: any;
  usedAllTimeslot?: boolean;
  onToggleUsedAllTimeslot?: () => void;
}

export default function DateTimeSelector({
  timeSlotData,
  loading,
  onChangeTimeZone,
  timeZone,
  coach,
  availableTimeSlotData = [],
  isCoachOwner = false,
  onSelectedBookingTimes,
  isMultiselect = false,
  viewMode = "vertical",
  selectedSlots = [],
  submitButton,
  usedAllTimeslot = false,
  onToggleUsedAllTimeslot,
}: DateTimeSlotSelectorProps) {
  const [date, setDate] = useState(null);
  const [displayMode, setDisplayMode] = useState("time");

  const timeslots = useMemo(
    () => (!date ? [] : getTimeslotsByDate(timeSlotData, date, timeZone)),
    [timeSlotData, date, timeZone],
  );

  const availableTimeslots = useMemo(
    () =>
      !date ? [] : getTimeslotsByDate(availableTimeSlotData, date, timeZone),
    [availableTimeSlotData, date, timeZone],
  );

  const availableDateCheckerMap = useMemo(
    () => mapCalendarDate(timeSlotData, timeZone),
    [timeSlotData, timeZone],
  );

  const isDisabled = useCallback(
    (date) => {
      const dateString = moment(date).format("MM-DD-YYYY");
      return !availableDateCheckerMap[dateString];
    },
    [availableDateCheckerMap],
  );

  useEffect(() => {
    if (!date) {
      const dateString = Object.keys(availableDateCheckerMap)[0];
      if (dateString) {
        const initDate = new Date(dateString.replaceAll("-", "/"));
        setDate(initDate);
      }
    }
  }, [date, availableDateCheckerMap]);

  const toggleDisplayMode = () => {
    setDisplayMode(displayMode === "time" ? "calendar" : "time");
  };

  const handleSelectedBookingTime = (timeSlots: TimeSlot[]) => {
    const firstTimeSlot = timeSlots?.[0]?.timeSlot;
    if (firstTimeSlot && moment(firstTimeSlot).isValid()) {
      onSelectedBookingTimes(timeSlots);
    } else {
      onSelectedBookingTimes([]);
    }
  };

  const nextDay = (numberDay) => {
    setDate(moment(date).add(numberDay, "days"));
  };

  const selectedDate = moment(date);
  const isMobile = useMediaQuery(isMobileQuery);
  const isHorizontalMode = viewMode === "horizontal" && !isMobile;

  const toggleSelectTimeSlot = (timeSlot: TimeSlot) => {
    const newTimeslots = getNewSelectedTimeSlots(
      timeSlot,
      isMultiselect,
      selectedSlots,
    );
    handleSelectedBookingTime(newTimeslots);
  };

  const togglePreferredSlots = (timeSlot: TimeSlot) => {
    const newTimeslots = selectedSlots.map((item) => {
      return item.timeSlot === timeSlot.timeSlot
        ? { ...item, isPreferred: !item.isPreferred }
        : item;
    });
    handleSelectedBookingTime(newTimeslots);
  };

  return (
    <Flex>
      <Box flex={1}>
        {isHorizontalMode && (
          <Box mt={12}>
            <RequestBookingText hasBorder />
          </Box>
        )}
        {selectedDate.isValid() && displayMode === "time" ? (
          <Flex
            justifyContent="space-between"
            alignItems="center"
            className="date-label"
          >
            <Clickable onClick={() => nextDay(-1)}>
              <Icon icon="arrow-left" />
            </Clickable>
            <Button
              onClick={toggleDisplayMode}
              icon="calendar"
              small={true}
              style={{ padding: "0 12px" }}
            >
              <Box color={CTA_COLOR}>
                {selectedDate.format("dddd, MMMM DD")}
              </Box>
            </Button>
            <Clickable onClick={() => nextDay(1)}>
              <Icon icon="arrow-right" />
            </Clickable>
          </Flex>
        ) : null}
        <TimeslotList
          className={`timeslot-list timeslot-grid ${
            isHorizontalMode && "timeslot-horizontal"
          }`}
          style={{ display: displayMode === "time" ? "block" : "none" }}
        >
          {selectedDate.isValid() ? (
            <>
              <TimeSlotSelector
                timeSlots={timeslots}
                availableTimeSlots={availableTimeslots}
                loading={loading}
                timeZone={timeZone}
                onChangeTimeSlots={handleSelectedBookingTime}
                itemCss={{
                  width: "100%",
                  fontSize: "16px !important",
                  padding: "10px 0px !important",
                }}
                isCoachOwner={isCoachOwner}
                maxShowByDefault={15}
                isMultiselect={isMultiselect}
                visibleTimeZone={!isHorizontalMode}
                visibleSelectedtimes={!isHorizontalMode}
                onChangeTimeZone={onChangeTimeZone}
                selectedSlots={selectedSlots}
              />
            </>
          ) : (
            <Flex
              mt={3}
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              p={3}
              backgroundColor={LIGHTEST_BACKGROUND}
              className="round-corners"
            >
              <Box fontSize={16} fontWeight={500}>
                Please select a date
              </Box>
            </Flex>
          )}
        </TimeslotList>
        <Flex
          className="sts-row"
          mt={0}
          style={{
            display: displayMode === "calendar" ? "flex" : "none",
            justifyContent: isHorizontalMode ? "center" : "center",
          }}
        >
          <Box
            className="calendar-wrapper"
            css={{ marginRight: "0 !important" }}
          >
            <Box className="calendar-ratio">
              <Box className="inner-ration" width={1}>
                <Calendar
                  className={IS_EXEC ? "exec-calendar" : "placement-calendar"}
                  value={date}
                  startOfWeek={0}
                  onChange={(newDate) => {
                    setDate(newDate);
                    toggleDisplayMode();
                  }}
                  isDisabled={(date) => {
                    return (
                      moment(date).isBefore(moment(), "day") || isDisabled(date)
                    );
                  }}
                  fontSize={18}
                  weekends={[]}
                  isMultiSelector={false}
                />
              </Box>
            </Box>
          </Box>
        </Flex>
      </Box>
      {isHorizontalMode && (
        <Box flex={1}>
          <Box className="timezone-wrapper" px={4} position="sticky" top={70}>
            <AllTimeslotToggle
              usedAllTimeslot={usedAllTimeslot}
              onToggleUsedAllTimeslot={onToggleUsedAllTimeslot}
              coach={coach}
            />

            <TimeZoneSelectorContainerStyled>
              <TimeZoneSelector value={timeZone} onChange={onChangeTimeZone} />
            </TimeZoneSelectorContainerStyled>

            <SelectedTimeSlots
              selectedSlots={selectedSlots}
              timeZone={timeZone}
              unSelectTimeSlot={toggleSelectTimeSlot}
              togglePreferredSlots={togglePreferredSlots}
              isHorizontalMode={isHorizontalMode}
            />
            <SubmitButtonContainer>{submitButton}</SubmitButtonContainer>
          </Box>
        </Box>
      )}
    </Flex>
  );
}

const SubmitButtonContainer = styled(Box)`
  position: sticky;
  bottom: 10px;
  .book-meeting-btn {
    padding-right: 0;
    margin-top: 10px;
  }
`;
